import { Component, computed, HostBinding, inject, input, InputSignal, Signal, Type, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UserIconComponent } from '../../user-icon/user-icon.component';
import { MDPopupComponent, MDIconComponent } from '@melodic-ui/components';
import { buildLessonTags } from '../utils/lesson-tags.util';
import { LESSON_STATUS_CONFIG } from '../utils/lesson-status-config';
import { AuthenticationService } from '../../../auth/services/authentication.service';
import { Role } from '../../../enums/role.enum';
import { ILessonListItem } from '@lb/api/mylb/interfaces';

@Component({
	selector: 'lb-lesson-popup',
	standalone: true,
	imports: [CommonModule, UserIconComponent, MDPopupComponent, MDIconComponent],
	templateUrl: './lesson-popup.component.html',
	styleUrl: './lesson-popup.component.scss',
	encapsulation: ViewEncapsulation.None
})
export class LessonPopupComponent {
	public lesson = input.required<ILessonListItem>();
	public lessonView: InputSignal<'learner' | 'instructor'> = input.required<'learner' | 'instructor'>();
	public lessonCardComponent: InputSignal<Type<Component> | null> = input<Type<Component> | null>(null);
	// Absolute route to the lesson-details page, supplied by the host calendar. Threaded through to
	// the rendered lesson card so its "Details" link resolves correctly from the calendar route
	// (the card's own relative fallback only works from a '.../lessons' list page).
	public detailsLink = input<(string | number)[] | null>(null);

	private _authService = inject(AuthenticationService);
	protected readonly statusConfig = LESSON_STATUS_CONFIG;

	// Determine view based on user role - instructors and admins see learner view, learners see instructor view
	private _viewForTags = computed(() => {
		const currentUser = this._authService.getUser();
		const isInstructorOrAdmin = currentUser?.roleID === Role.INSTRUCTOR || currentUser?.roleID === Role.ADMIN;
		return isInstructorOrAdmin ? 'instructor' : 'learner';
	});

	private _lessonTagSummary = computed(() => buildLessonTags({ lesson: this.lesson(), view: this._viewForTags() }));

	@HostBinding('class.completed')
	get isCompleted(): boolean {
		return this.lesson().attendanceStatusID === 1;
	}

	@HostBinding('class.absent')
	get isAbsent(): boolean {
		return this.lesson().attendanceStatusID === 2;
	}

	@HostBinding('class.has-warning')
	get hasWarningClass(): boolean {
		return this._lessonTagSummary().hasWarning;
	}

	@HostBinding('class.has-error')
	get hasErrorClass(): boolean {
		return this._lessonTagSummary().hasError;
	}

	public lessonStatus = computed(() => {
		// Get status based on severity of tags
		if (this._lessonTagSummary().tags.length > 0) {
			if (this._lessonTagSummary().hasError) {
				if (this._lessonTagSummary().tags.find((tag) => tag.text === 'Awaiting Attendance')) {
					return 'missing-attendance';
				}
			} else if (this._lessonTagSummary().hasWarning) {
				if (this._lessonTagSummary().tags.find((tag) => tag.text === 'Pending Reschedule')) {
					return 'reschedule';
				} else if (this._lessonTagSummary().tags.find((tag) => tag.text === 'Awaiting Payment')) {
					return 'awaiting-payment';
				}
			}
		}

		if (this.lesson().lessonStatusID === 5) {
			if (this.lesson().attendanceStatusID === 2) {
				return 'absent';
			} else {
				return 'completed';
			}
		}

		return 'scheduled';
	});

	public userID: Signal<number | undefined> = computed(() => {
		const lesson = this.lesson();
		const lessonViews: { [key: string]: () => number | undefined } = {
			learner: () => lesson.learnerUserID,
			instructor: () => lesson.instructorUserID
		};

		return lessonViews[this.lessonView()]();
	});
}
